import {FormControl, Select} from '@primer/react-brand'
import {t, getLocale} from '../../../../../../lib/utils/localization'
import * as z from 'zod'
import {useCallback, use, useMemo} from 'react'
import {FormContext} from '../../../FormContext'
import {getAnalyticsEvent} from '../../../../../../lib/utils/analytics'
import {ConsentExperienceContext} from '../ConsentExperienceContext'
import countriesEs from './countries/es'
import countriesKo from './countries/ko'
import countriesPt from './countries/pt'
import countriesJa from './countries/ja'

const validations = [
  {
    schema: z.string().min(1),
    message: t('Please select your country/region'),
  },
]

export const CountrySelectField = () => {
  const validationErrorId = 'country-validation-msg'
  const formContext = use(FormContext)
  const countryError = formContext?.errors['country']
  const {selectedCountry, setSelectedCountry} = use(ConsentExperienceContext)
  const ctx = use(ConsentExperienceContext)
  const locale = getLocale()
  const countries = useMemo(() => {
    switch (locale) {
      case 'es':
        return countriesEs
      case 'ko':
        return countriesKo
      case 'pt':
        return countriesPt
      case 'ja':
        return countriesJa
      default:
        return ctx.marketingTargetedCountries || []
    }
  }, [ctx.marketingTargetedCountries, locale])

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedAlpha = e.target.value
      const country = countries.find(c => c.alpha === selectedAlpha) || null
      setSelectedCountry(country)
    },
    [countries, setSelectedCountry],
  )

  return (
    <FormControl
      id="form-field-country"
      validationStatus={typeof countryError === 'string' ? 'error' : undefined}
      fullWidth
      required
    >
      <FormControl.Label>{t('Country')}</FormControl.Label>

      <Select
        {...formContext?.register('country', {
          label: 'Country',
          required: true,
          validations,
        })}
        {...getAnalyticsEvent({
          action: 'country',
          tag: 'select',
          context: 'form_control',
          location: 'form',
        })}
        aria-describedby={validationErrorId}
        onChange={handleCountryChange}
        value={selectedCountry?.alpha || ''}
      >
        <Select.Option value="">{t('Choose your country/region')}</Select.Option>

        {countries.map(country => (
          <Select.Option key={country.name} value={country.alpha}>
            {country.name}
          </Select.Option>
        ))}
      </Select>

      {typeof countryError === 'string' ? (
        <FormControl.Validation id={validationErrorId}>{countryError}</FormControl.Validation>
      ) : null}
    </FormControl>
  )
}
